/**
 * The one handler for the device's Back key. Each press runs the first step
 * that handles it, in this order:
 *
 * 1. A component that already handled the key keeps it (`defaultPrevented`).
 * 2. The topmost closable closes. Closables follow the Escape protocol: they
 *    close on an Escape `keydown` and cancel it. Back sends one to the focused
 *    element inside the topmost open dialog or menu, else to that overlay, else
 *    to the focused element (a panel, a page in an edit mode).
 * 3. The topmost full-screen overlay closes (the mobile full player, a
 *    full-screen right panel or chat pane), then the browser leaves full screen.
 * 4. Added steps run, newest first (the TV's page → menu move).
 * 5. The router goes back.
 *
 * A raw Escape the key map has neither as `escape` nor as `back` (a TV's) is
 * dropped before anything sees it.
 */
import { globalRef, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { type KeyMap, matchesKey } from '@/device/input/KeyMap'
import { isTextEntry, windowEvents } from '@/util/interaction'
import { closeTopFullScreenOverlay } from '@/util/fullScreenOverlayRegistry'

/** A Back step; returns true when it handled the press. */
export type BackStep = () => boolean

const OVERLAYS = '[aria-modal="true"], [role="dialog"], [role="menu"]'

class CloseRequest extends Event {
    readonly key = 'Escape'
    readonly code = 'Escape'

    constructor() {
        super('keydown', { bubbles: true, cancelable: true })
    }
}

const backStepsRef = globalRef(() => ({ steps: [] as BackStep[] }))

/** Adds a step that runs once nothing closes, before the router goes back. */
export function addBackStep(step: BackStep): Unsubscribe {
    const { steps } = backStepsRef.value
    steps.unshift(step)
    return () => {
        const at = steps.indexOf(step)
        if (at >= 0) steps.splice(at, 1)
    }
}

/** Whether `event` is the key map's Back, short of a Backspace a text field keeps. */
export function isBackKey(event: KeyboardEvent, keyMap: KeyMap): boolean {
    if (!matchesKey(event, keyMap.back)) return false
    return !(event.key === 'Backspace' && isTextEntry(event.target))
}

function hasArea(el: Element): boolean {
    const { width, height } = el.getBoundingClientRect()
    return width > 0 && height > 0
}

function closableTarget(): Element {
    const active = document.activeElement ?? document.body
    const overlay = Array.from(document.querySelectorAll(OVERLAYS)).filter(hasArea).at(-1)
    return overlay && !overlay.contains(active) ? overlay : active
}

/** Asks the topmost closable to close; true when one did. */
export function closeTopmost(event: KeyboardEvent): boolean {
    const target = closableTarget()
    // A real Escape has already been offered to everything on its own path.
    if (event.key === 'Escape' && target.contains(event.target as Node | null)) return false
    const request = new CloseRequest()
    target.dispatchEvent(request)
    return request.defaultPrevented
}

function exitFullScreen(): boolean {
    if (!document.fullscreenElement) return false
    document.exitFullscreen().catch(noop)
    return true
}

/** Stops a raw Escape the key map has no key for, so closables answer only to Back's request. */
function dropForeignEscape(keyMap: KeyMap): Unsubscribe {
    return windowEvents.on(
        'keydown',
        (event) => {
            if (event instanceof CloseRequest || event.key !== 'Escape') return
            if (matchesKey(event, keyMap.escape) || matchesKey(event, keyMap.back)) return
            event.preventDefault()
            event.stopImmediatePropagation()
        },
        { capture: true }
    )
}

/** Binds the key map's Back to the order above, ending in `navigateBack`. */
export function bindBackKey(keyMap: KeyMap, navigateBack: () => void): Unsubscribe {
    const stopForeignEscape = dropForeignEscape(keyMap)
    const stopBack = windowEvents.on('keydown', (event) => {
        if (event.defaultPrevented || event instanceof CloseRequest) return
        if (!isBackKey(event, keyMap)) return
        event.preventDefault()
        if (closeTopmost(event) || closeTopFullScreenOverlay() || exitFullScreen()) return
        if (backStepsRef.value.steps.some((step) => step())) return
        navigateBack()
    })
    return () => {
        stopForeignEscape()
        stopBack()
    }
}
